/**
 * Authentication Store using Zustand
 */
import { create } from 'zustand';
import api from './api';

const useAuthStore = create((set, get) => ({
  user: null,
  token: localStorage.getItem('token'),
  isAuthenticated: !!localStorage.getItem('token'),
  isLoading: !!localStorage.getItem('token'),
  error: null,
  mfaRequired: false,
  mfaToken: null,
  mfaMethod: null,
  mfaPhoneMasked: null,
  lockoutSeconds: 0,
  lockoutMessage: '',
  // Impersonation
  originalToken: localStorage.getItem('originalToken') || null,
  isImpersonating: !!localStorage.getItem('originalToken'),

  login: async (email, password) => {
    set({ error: null, mfaRequired: false, mfaToken: null, mfaMethod: null, mfaPhoneMasked: null, lockoutSeconds: 0, lockoutMessage: '' });
    try {
      const data = await api.login(email, password);

      // Check if MFA is required
      if (data.mfa_required) {
        set({
          mfaRequired: true,
          mfaToken: data.mfa_token,
          mfaMethod: data.mfa_method || 'totp',
          mfaPhoneMasked: data.phone_masked || data.email_masked || null,
          isLoading: false,
        });
        return 'mfa_required';
      }

      localStorage.setItem('token', data.access_token);
      if (data.refresh_token) localStorage.setItem('refresh_token', data.refresh_token);
      
      const user = await api.getCurrentUser();
      set({ user, token: data.access_token, isAuthenticated: true, isLoading: false });
      
      return true;
    } catch (error) {
      // Handle lockout (429)
      if (error.status === 429 || error.lockoutData) {
        const ld = error.lockoutData || {};
        set({
          isLoading: false,
          error: ld.message || 'Account locked',
          lockoutSeconds: ld.remaining_seconds || 300,
          lockoutMessage: ld.message || 'Account temporarily locked. Too many failed attempts.',
        });
        return 'locked';
      }
      set({ error: error.message, isLoading: false });
      // 403 = valid credentials but the account/organization can't sign in yet (pending, suspended, closed).
      return error.status === 403 ? 'blocked' : false;
    }
  },

  verifyMfa: async (code) => {
    const { mfaToken } = get();
    set({ isLoading: true, error: null });
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/mfa/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mfa_token: mfaToken, code }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: 'Invalid code' }));
        throw new Error(err.detail || 'MFA verification failed');
      }
      const data = await res.json();
      localStorage.setItem('token', data.access_token);
      if (data.refresh_token) localStorage.setItem('refresh_token', data.refresh_token);

      const user = await api.getCurrentUser();
      set({ user, token: data.access_token, isAuthenticated: true, isLoading: false, mfaRequired: false, mfaToken: null });
      return true;
    } catch (error) {
      set({ error: error.message, isLoading: false });
      return false;
    }
  },

  devLogin: async (role = 'iso_agent') => {
    set({ isLoading: true, error: null });
    try {
      const data = await api.devLogin(role);
      localStorage.setItem('token', data.access_token);
      
      const user = await api.getCurrentUser();
      set({ user, token: data.access_token, isAuthenticated: true, isLoading: false });
      
      return true;
    } catch (error) {
      set({ error: error.message, isLoading: false });
      return false;
    }
  },

  demoVerify: async (token) => {
    set({ isLoading: true, error: null });
    try {
      const data = await api.verifyDemoToken(token);
      localStorage.setItem('token', data.access_token);
      const user = await api.getCurrentUser();
      localStorage.setItem('start_demo_tour', '1');
      set({ user, token: data.access_token, isAuthenticated: true, isLoading: false });
      return true;
    } catch (error) {
      set({ error: error.message, isLoading: false });
      return false;
    }
  },

  demoLogin: async () => {
    set({ isLoading: true, error: null });
    try {
      const data = await api.demoLogin();
      localStorage.setItem('token', data.access_token);
      if (data.refresh_token) localStorage.setItem('refresh_token', data.refresh_token);

      const user = await api.getCurrentUser();
      localStorage.setItem('start_demo_tour', '1');
      set({ user, token: data.access_token, isAuthenticated: true, isLoading: false });

      return true;
    } catch (error) {
      set({ error: error.message, isLoading: false });
      return false;
    }
  },

  logout: async () => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/logout`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        }).catch(() => {});
      }
    } catch {}
    localStorage.removeItem('token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('originalToken');
    set({ user: null, token: null, isAuthenticated: false, originalToken: null, isImpersonating: false, mfaRequired: false, mfaToken: null });
  },

  loadUser: async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      set({ isAuthenticated: false });
      return;
    }

    set({ isLoading: true });
    try {
      const user = await api.getCurrentUser();
      set({ user, isAuthenticated: true, isLoading: false });
    } catch (error) {
      localStorage.removeItem('token');
      set({ user: null, token: null, isAuthenticated: false, isLoading: false });
    }
  },

  startImpersonation: async (newToken) => {
    const currentToken = localStorage.getItem('token');
    localStorage.setItem('originalToken', currentToken);
    localStorage.setItem('token', newToken);
    set({ originalToken: currentToken, isImpersonating: true, token: newToken });
    try {
      const user = await api.getCurrentUser();
      set({ user, isAuthenticated: true });
    } catch {
      get().stopImpersonation();
    }
  },

  stopImpersonation: () => {
    const originalToken = localStorage.getItem('originalToken');
    if (originalToken) {
      localStorage.setItem('token', originalToken);
      localStorage.removeItem('originalToken');
      set({ originalToken: null, isImpersonating: false, token: originalToken, isLoading: true });
      get().loadUser();
    }
  },
}));

export default useAuthStore;
